import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ShieldCheck, Users, Banknote, FileText, Settings, Activity, Mail, Download, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

export default function AdminPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loans, setLoans] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({ btc_address: "", eth_address: "", usdt_address: "" });
  const [emailCfg, setEmailCfg] = useState({
    provider: "", enabled: true, from_name: "Duocapital", from_email: "",
    smtp: { host: "", port: 587, username: "", password: "", encryption: "tls" },
    sendgrid: { api_key: "" }, resend: { api_key: "" },
    smtp_status: null, sendgrid_configured: false, resend_configured: false,
  });
  const [testRecipient, setTestRecipient] = useState("");
  const [trade, setTrade] = useState({ user_id: "", pair: "BTC/USDT", side: "Buy", price: 67000, amount: 0.01, profit: 100 });

  const loadAll = async () => {
    const [u, i, l, w, m, s, e] = await Promise.all([
      api.get("/admin/users"), api.get("/admin/investments"), api.get("/admin/loans"),
      api.get("/admin/withdrawals"), api.get("/admin/contact"), api.get("/admin/settings"),
      api.get("/admin/email-settings"),
    ]);
    setUsers(u.data); setInvestments(i.data); setLoans(l.data);
    setWithdrawals(w.data); setMessages(m.data);
    setSettings({ ...settings, ...s.data });
    setEmailCfg(prev => ({
      ...prev,
      provider: e.data.provider || "",
      enabled: e.data.enabled !== false,
      from_name: e.data.from_name || "Duocapital",
      from_email: e.data.from_email || "",
      smtp: {
        host: e.data.smtp?.host || "",
        port: e.data.smtp?.port || 587,
        username: e.data.smtp?.username || "",
        password: "",
        encryption: e.data.smtp?.encryption || "tls",
      },
      smtp_status: e.data.smtp?.has_password,
      sendgrid: { api_key: "" },
      sendgrid_configured: e.data.sendgrid_configured,
      resend: { api_key: "" },
      resend_configured: e.data.resend_configured,
    }));
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const updateUser = async (id, patch) => {
    try { await api.patch(`/admin/users/${id}`, patch); toast.success("User updated"); loadAll(); }
    catch { toast.error("Failed"); }
  };

  const deleteUser = async (id, email) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(`User ${email} deleted with all data`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const decideLoan = async (id, status) => {
    try { await api.patch(`/admin/loans/${id}`, { status }); toast.success(`Loan ${status}`); loadAll(); }
    catch { toast.error("Failed"); }
  };

  const deleteLoan = async (id) => {
    try {
      await api.delete(`/admin/loans/${id}`);
      toast.success("Loan entry deleted");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const updateInvestment = async (id, patch) => {
    try { await api.patch(`/admin/investments/${id}`, patch); toast.success("Investment updated"); loadAll(); }
    catch { toast.error("Failed"); }
  };

  const updateWithdrawal = async (id, status) => {
    try { await api.patch(`/admin/withdrawals/${id}?status=${status}`); toast.success(`Withdrawal ${status}`); loadAll(); }
    catch { toast.error("Failed"); }
  };
  const saveSettings = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Settings saved"); }
    catch { toast.error("Failed"); }
  };
  const saveEmailCfg = async () => {
    try {
      const payload = {
        provider: emailCfg.provider || null,
        enabled: emailCfg.enabled,
        from_name: emailCfg.from_name,
        from_email: emailCfg.from_email || null,
        smtp: emailCfg.provider === "smtp" ? {
          host: emailCfg.smtp.host, port: Number(emailCfg.smtp.port),
          username: emailCfg.smtp.username, encryption: emailCfg.smtp.encryption,
          ...(emailCfg.smtp.password ? { password: emailCfg.smtp.password } : {}),
        } : null,
        sendgrid: emailCfg.provider === "sendgrid" ? (emailCfg.sendgrid.api_key ? { api_key: emailCfg.sendgrid.api_key } : {}) : null,
        resend: emailCfg.provider === "resend" ? (emailCfg.resend.api_key ? { api_key: emailCfg.resend.api_key } : {}) : null,
      };
      await api.put("/admin/email-settings", payload);
      toast.success("Email settings saved");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sendTest = async () => {
    if (!testRecipient) { toast.error("Enter a recipient email"); return; }
    try {
      const r = await api.post(`/admin/email-settings/test?to=${encodeURIComponent(testRecipient)}`);
      if (r.data.ok) toast.success(r.data.message);
      else toast.error(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const exportCsv = async (entity) => {
    try {
      const r = await api.get(`/admin/export/${entity}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `duocapital_${entity}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${entity}.csv`);
    } catch (e) { toast.error("Export failed"); }
  };

  const ExportBtn = ({ entity }) => (
    <button onClick={() => exportCsv(entity)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-white/5 hover:bg-[#D4AF37]/20 hover:text-[#D4AF37] border border-white/10 transition-colors" data-testid={`export-${entity}`}>
      <Download className="w-3.5 h-3.5" /> Export CSV
    </button>
  );
  const addTrade = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/trades", trade); toast.success("Trade injected — user balance updated"); loadAll(); }
    catch { toast.error("Failed"); }
  };

  const onLogout = () => { logout(); nav("/"); };

  const TABS = [
    ["users", "Users", Users],
    ["investments", "Investments", FileText],
    ["loans", "Loans", Banknote],
    ["withdrawals", "Withdrawals", Banknote],
    ["trades", "Trade Injection", Activity],
    ["messages", "Messages", Mail],
    ["email", "Email & SMTP", Mail],
    ["settings", "Wallet Settings", Settings],
  ];

  return (
    <div className="min-h-screen" data-testid="admin-page">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0A0E27]/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
            <div>
              <div className="font-display text-lg font-bold">Duocapital Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-[#00D68F]">Secure session • {user?.email}</div>
            </div>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="space-y-1">
          {TABS.map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} className={`w-full text-left flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${tab === k ? "bg-[#D4AF37] text-[#0A0E27] font-bold" : "text-white/70 hover:bg-white/5"}`} data-testid={`admin-tab-${k}`}>
              <I className="w-4 h-4" /> {l}
            </button>
          ))}
        </aside>

        <main className="min-w-0">
          {tab === "users" && (
            <div className="glass-strong rounded-2xl p-5 overflow-x-auto" data-testid="admin-users">
              <div className="flex justify-end mb-3"><ExportBtn entity="users" /></div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-widest text-white/50 border-b border-white/10">
                  <th className="py-3 pr-3">User</th><th>Balance</th><th>Returns</th><th>Loan</th><th>KYC</th><th>Bot</th><th></th>
                </tr></thead>
                <tbody>
                  {users.map(u => (
                    <UserRow key={u.id} u={u} onSave={updateUser} onDelete={deleteUser} onRefresh={loadAll} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "investments" && (
            <div className="glass-strong rounded-2xl p-5 overflow-x-auto" data-testid="admin-investments">
              <div className="flex justify-end mb-3"><ExportBtn entity="investments" /></div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-widest text-white/50 border-b border-white/10">
                  <th className="py-3">User</th><th>Tier</th><th>Amount</th><th>Returns</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {investments.map(inv => {
                    const u = users.find(x => x.id === inv.user_id);
                    return <InvRow key={inv.id} inv={inv} userName={u?.full_name || inv.user_id} onSave={updateInvestment} />;
                  })}
                </tbody>
              </table>
            </div>
          )}

          {tab === "loans" && (
            <div className="glass-strong rounded-2xl p-5 overflow-x-auto" data-testid="admin-loans">
              <div className="flex justify-end mb-3"><ExportBtn entity="loans" /></div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-widest text-white/50 border-b border-white/10"><th className="py-3">User</th><th>Amount</th><th>Tier</th><th>Status</th><th></th></tr></thead>
                <tbody>
                    {loans.map(l => (
                      <tr key={l.id} className="border-b border-white/5">
                        <td className="py-3">{l.full_name}</td>
                        <td>${l.amount?.toLocaleString()}</td>
                        <td className="capitalize">{l.tier}</td>
                        <td><span className="text-yellow-300 capitalize">{l.status?.replace("_", " ")}</span></td>
                        <td className="flex gap-2 py-3">
                          <button onClick={() => decideLoan(l.id, "approved")} className="px-3 py-1 rounded text-xs bg-[#00D68F]/20 text-[#00D68F] hover:bg-[#00D68F]/30">Approve</button>
                          <button onClick={() => decideLoan(l.id, "rejected")} className="px-3 py-1 rounded text-xs bg-red-500/20 text-red-300 hover:bg-red-500/30">Reject</button>
                          <button onClick={() => deleteLoan(l.id)} className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30" title="Delete loan entry">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "withdrawals" && (
            <div className="glass-strong rounded-2xl p-5 overflow-x-auto" data-testid="admin-withdrawals">
              <div className="flex justify-end mb-3"><ExportBtn entity="withdrawals" /></div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-widest text-white/50 border-b border-white/10"><th className="py-3">User</th><th>Amount</th><th>Crypto</th><th>Wallet</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {withdrawals.map(w => (
                    <tr key={w.id} className="border-b border-white/5">
                      <td className="py-3">{users.find(u => u.id === w.user_id)?.full_name || "—"}</td>
                      <td>${w.amount?.toLocaleString()}</td>
                      <td>{w.crypto}</td>
                      <td className="font-mono text-[10px] max-w-[16ch] truncate">{w.wallet_address}</td>
                      <td className="capitalize">{w.status}</td>
                      <td className="flex gap-2 py-3">
                        <button onClick={() => updateWithdrawal(w.id, "paid")} className="px-3 py-1 rounded text-xs bg-[#00D68F]/20 text-[#00D68F]">Paid</button>
                        <button onClick={() => updateWithdrawal(w.id, "rejected")} className="px-3 py-1 rounded text-xs bg-red-500/20 text-red-300">Reject</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "trades" && (
            <form onSubmit={addTrade} className="glass-strong rounded-2xl p-6 space-y-4 max-w-lg" data-testid="admin-trades">
              <div className="label-eyebrow">Inject Trade Activity</div>
              <p className="text-sm text-white/60">Adds a fake trade to the user's feed AND credits their balance with the profit amount.</p>
              <div>
                <label className="text-xs uppercase text-white/60 block mb-2">User</label>
                <select value={trade.user_id} onChange={e => setTrade({ ...trade, user_id: e.target.value })} className="w-full px-4 py-3 rounded-lg" required>
                  <option value="">Select user</option>
                  {users.filter(u => u.role !== "admin").map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Pair" value={trade.pair} onChange={e => setTrade({ ...trade, pair: e.target.value })} />
                <Field label="Side" value={trade.side} onChange={e => setTrade({ ...trade, side: e.target.value })} />
                <Field label="Price" type="number" value={trade.price} onChange={e => setTrade({ ...trade, price: Number(e.target.value) })} />
                <Field label="Amount" type="number" step="0.0001" value={trade.amount} onChange={e => setTrade({ ...trade, amount: Number(e.target.value) })} />
                <Field label="Profit ($)" type="number" value={trade.profit} onChange={e => setTrade({ ...trade, profit: Number(e.target.value) })} />
              </div>
              <button type="submit" className="btn-primary w-full">Inject Trade</button>
            </form>
          )}

          {tab === "messages" && (
            <div className="glass-strong rounded-2xl p-5 space-y-3" data-testid="admin-messages">
              <div className="flex justify-end mb-1"><ExportBtn entity="contact" /></div>
              {messages.map(m => (
                <div key={m.id} className="p-4 rounded-lg bg-white/5">
                  <div className="flex justify-between mb-1">
                    <div className="font-semibold">{m.name} <span className="text-white/40 text-xs">• {m.email}</span></div>
                    <div className="text-xs text-white/40">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-[#D4AF37]">{m.subject}</div>
                  <div className="text-sm text-white/70 mt-2">{m.message}</div>
                </div>
              ))}
              {messages.length === 0 && <div className="text-white/50 text-sm">No messages.</div>}
            </div>
          )}

          {tab === "email" && (
            <div className="glass-strong rounded-2xl p-6 space-y-5 max-w-3xl" data-testid="admin-email-settings">
              <div>
                <div className="label-eyebrow">Mail Configuration</div>
                <h2 className="font-display text-2xl font-bold mt-1">Email & SMTP</h2>
                <p className="text-sm text-white/60 mt-2">Configure how transactional emails are sent (welcome, investment, loan, withdrawal, contact). Works with Gmail App Passwords, Outlook, Zoho, Mailgun SMTP, custom SMTP servers, SendGrid, and Resend.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="From Name" value={emailCfg.from_name} onChange={e => setEmailCfg({...emailCfg, from_name: e.target.value})} placeholder="Duocapital" />
                <Field label="From Email" type="email" value={emailCfg.from_email} onChange={e => setEmailCfg({...emailCfg, from_email: e.target.value})} placeholder="noreply@duocapital.com" />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Provider</label>
                <select value={emailCfg.provider} onChange={e => setEmailCfg({...emailCfg, provider: e.target.value})} className="w-full px-3 py-2 rounded-lg text-sm" data-testid="email-provider">
                  <option value="">— Disabled (no emails will be sent) —</option>
                  <option value="smtp">Generic SMTP (Gmail / Outlook / Zoho / Mailgun / custom)</option>
                  <option value="sendgrid">SendGrid API</option>
                  <option value="resend">Resend API</option>
                </select>
              </div>

              {emailCfg.provider === "smtp" && (
                <div className="glass rounded-xl p-4 space-y-3" data-testid="email-smtp-block">
                  <div className="label-eyebrow">SMTP Server</div>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Host" value={emailCfg.smtp.host} onChange={e => setEmailCfg({...emailCfg, smtp: {...emailCfg.smtp, host: e.target.value}})} placeholder="smtp.gmail.com" />
                    <Field label="Port" type="number" value={emailCfg.smtp.port} onChange={e => setEmailCfg({...emailCfg, smtp: {...emailCfg.smtp, port: Number(e.target.value)}})} />
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/60 block mb-1">Encryption</label>
                      <select value={emailCfg.smtp.encryption} onChange={e => setEmailCfg({...emailCfg, smtp: {...emailCfg.smtp, encryption: e.target.value}})} className="w-full px-3 py-2 rounded-lg text-sm">
                        <option value="tls">TLS / STARTTLS</option>
                        <option value="ssl">SSL</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Username" value={emailCfg.smtp.username} onChange={e => setEmailCfg({...emailCfg, smtp: {...emailCfg.smtp, username: e.target.value}})} placeholder="you@gmail.com" />
                    <Field label={emailCfg.smtp_status ? "Password (leave blank to keep)" : "Password / App Password"} type="password" value={emailCfg.smtp.password} onChange={e => setEmailCfg({...emailCfg, smtp: {...emailCfg.smtp, password: e.target.value}})} placeholder={emailCfg.smtp_status ? "•••••••• (saved)" : "Enter password"} />
                  </div>
                  <div className="text-xs text-white/50">
                    <strong className="text-[#D4AF37]">Gmail tip:</strong> use an <a className="underline" target="_blank" rel="noreferrer" href="https://myaccount.google.com/apppasswords">App Password</a> (requires 2-Step Verification). Host: <code>smtp.gmail.com</code>, Port: <code>587</code>, Encryption: <code>TLS</code>.
                  </div>
                </div>
              )}

              {emailCfg.provider === "sendgrid" && (
                <div className="glass rounded-xl p-4 space-y-3" data-testid="email-sendgrid-block">
                  <div className="label-eyebrow">SendGrid API</div>
                  <Field label={emailCfg.sendgrid_configured ? "API Key (leave blank to keep existing)" : "API Key"} type="password" value={emailCfg.sendgrid.api_key} onChange={e => setEmailCfg({...emailCfg, sendgrid: {api_key: e.target.value}})} placeholder={emailCfg.sendgrid_configured ? "•••• (saved)" : "SG.xxxxx"} />
                  <div className="text-xs text-white/50">Get yours at <a className="underline" target="_blank" rel="noreferrer" href="https://app.sendgrid.com/settings/api_keys">sendgrid.com → API Keys</a>. Make sure your sender email is verified.</div>
                </div>
              )}

              {emailCfg.provider === "resend" && (
                <div className="glass rounded-xl p-4 space-y-3" data-testid="email-resend-block">
                  <div className="label-eyebrow">Resend API</div>
                  <Field label={emailCfg.resend_configured ? "API Key (leave blank to keep existing)" : "API Key"} type="password" value={emailCfg.resend.api_key} onChange={e => setEmailCfg({...emailCfg, resend: {api_key: e.target.value}})} placeholder={emailCfg.resend_configured ? "•••• (saved)" : "re_xxxxx"} />
                  <div className="text-xs text-white/50">Get yours at <a className="underline" target="_blank" rel="noreferrer" href="https://resend.com/api-keys">resend.com → API Keys</a>. Verify your sending domain first.</div>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={emailCfg.enabled} onChange={e => setEmailCfg({...emailCfg, enabled: e.target.checked})} /> Enable transactional emails
              </label>

              <div className="flex gap-3">
                <button onClick={saveEmailCfg} className="btn-primary" data-testid="email-save">Save Email Settings</button>
              </div>

              <div className="h-px bg-white/10"></div>

              <div>
                <div className="label-eyebrow mb-2">Send Test Email</div>
                <div className="flex gap-2">
                  <input type="email" placeholder="test-recipient@example.com" value={testRecipient} onChange={e => setTestRecipient(e.target.value)} className="flex-1 px-4 py-2 rounded-lg text-sm" data-testid="email-test-to" />
                  <button onClick={sendTest} className="btn-outline" data-testid="email-test-send">Send Test</button>
                </div>
                <div className="text-xs text-white/50 mt-2">Save your settings first, then click "Send Test" to verify deliverability.</div>
              </div>

              <div className="glass rounded-xl p-4 text-xs text-white/60 space-y-1">
                <div className="text-[#D4AF37] font-semibold">Triggers covered automatically:</div>
                <div>• Welcome on registration · Investment submitted · Loan approved/rejected · Withdrawal requested/paid · AI Bot activation · Contact form auto-reply</div>
              </div>
            </div>
          )}

          {tab === "settings" && (
            <div className="glass-strong rounded-2xl p-6 space-y-4 max-w-2xl" data-testid="admin-settings">
              <div className="label-eyebrow">Crypto Wallet Addresses</div>
              <Field label="Bitcoin (BTC)" value={settings.btc_address || ""} onChange={e => setSettings({ ...settings, btc_address: e.target.value })} />
              <Field label="Ethereum (ETH)" value={settings.eth_address || ""} onChange={e => setSettings({ ...settings, eth_address: e.target.value })} />
              <Field label="USDT (TRC-20)" value={settings.usdt_address || ""} onChange={e => setSettings({ ...settings, usdt_address: e.target.value })} />
              <button onClick={saveSettings} className="btn-primary">Save Settings</button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function UserRow({ u, onSave, onDelete, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [p, setP] = useState({
    balance: u.balance || 0,
    total_returns: u.total_returns || 0,
    loan_amount: u.loan_amount || 0,
    kyc_status: u.kyc_status || "pending",
  });

  const handleDelete = () => {
    onDelete(u.id, u.email);
    setDeleteConfirm(false);
  };

  const approveBot = async () => {
    try {
      await api.patch(`/admin/users/${u.id}/approve-bot`);
      toast.success("AI Bot approved and activated");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve bot");
    }
  };

  const rejectBot = async () => {
    try {
      await api.patch(`/admin/users/${u.id}/reject-bot`);
      toast.success("AI Bot subscription rejected");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject bot");
    }
  };

  return (
    <>
      <tr className="border-b border-white/5">
        <td className="py-3 pr-3">
          <div className="font-medium">{u.full_name}</div>
          <div className="text-xs text-white/50">{u.email} {u.account_type === "joint" && <span className="text-[#D4AF37]">• Joint</span>}</div>
        </td>
        {editing ? (
          <>
            <td><MiniInput type="number" value={p.balance} onChange={e => setP({...p, balance: Number(e.target.value)})} /></td>
            <td><MiniInput type="number" value={p.total_returns} onChange={e => setP({...p, total_returns: Number(e.target.value)})} /></td>
            <td><MiniInput type="number" value={p.loan_amount} onChange={e => setP({...p, loan_amount: Number(e.target.value)})} /></td>
            <td>
              <select value={p.kyc_status} onChange={e => setP({...p, kyc_status: e.target.value})} className="px-2 py-1 text-xs">
                <option value="pending">pending</option><option value="verified">verified</option>
              </select>
            </td>
            <td className="text-xs">
              {u.ai_bot_active ? <span className="text-[#00D68F]">ON ({u.ai_bot_tier})</span> : u.ai_bot_pending ? <span className="text-yellow-300">PENDING</span> : <span className="text-white/40">OFF</span>}
            </td>
            <td className="py-2 flex gap-1">
              <button onClick={() => { onSave(u.id, p); setEditing(false); }} className="px-2 py-1 text-xs bg-[#00D68F]/20 text-[#00D68F] rounded">Save</button>
              <button onClick={() => setEditing(false)} className="px-2 py-1 text-xs bg-white/10 rounded">Cancel</button>
            </td>
          </>
        ) : (
          <>
            <td>${(u.balance || 0).toLocaleString()}</td>
            <td className="text-[#00D68F]">${(u.total_returns || 0).toLocaleString()}</td>
            <td>${(u.loan_amount || 0).toLocaleString()}</td>
            <td className="capitalize text-xs">{u.kyc_status || "pending"}</td>
            <td className="text-xs">
              {u.ai_bot_active ? <span className="text-[#00D68F]">ON ({u.ai_bot_tier})</span> : u.ai_bot_pending ? <span className="text-yellow-300">PENDING ({u.ai_bot_tier})</span> : <span className="text-white/40">OFF</span>}
            </td>
            <td className="py-2 flex flex-wrap gap-1">
              {u.ai_bot_pending ? (
                <>
                  <button onClick={approveBot} className="px-3 py-1 text-xs bg-[#00D68F]/20 text-[#00D68F] rounded hover:bg-[#00D68F]/30">Approve Bot</button>
                  <button onClick={rejectBot} className="px-3 py-1 text-xs bg-red-500/20 text-red-300 rounded hover:bg-red-500/30">Reject Bot</button>
                </>
              ) : (
                <button onClick={() => setEditing(true)} className="px-3 py-1 text-xs bg-[#D4AF37]/20 text-[#D4AF37] rounded">Edit</button>
              )}
              <button onClick={() => setDeleteConfirm(true)} className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded hover:bg-red-500/30" title="Delete user">
                <Trash2 className="w-3 h-3" />
              </button>
            </td>
          </>
        )}
      </tr>

      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{u.full_name}</strong> ({u.email}) and all their associated data including investments, loans, withdrawals, and trades. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete Account</AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function InvRow({ inv, userName, onSave }) {
  const [editing, setEditing] = useState(false);
  const [p, setP] = useState({ status: inv.status, returns: inv.returns || 0 });

  const approve = () => onSave(inv.id, { status: "active" });
  const reject = () => onSave(inv.id, { status: "rejected" });

  return (
    <tr className="border-b border-white/5">
      <td className="py-3">{userName}</td>
      <td className="capitalize">{inv.tier}</td>
      <td>${inv.amount?.toLocaleString()}</td>
      {editing ? (
        <>
          <td><MiniInput type="number" value={p.returns} onChange={e => setP({...p, returns: Number(e.target.value)})} /></td>
          <td>
            <select value={p.status} onChange={e => setP({...p, status: e.target.value})} className="px-2 py-1 text-xs">
              <option value="pending">pending</option>
              <option value="active">active</option>
              <option value="completed">completed</option>
              <option value="rejected">rejected</option>
            </select>
          </td>
          <td className="flex gap-1">
            <button onClick={() => { onSave(inv.id, p); setEditing(false); }} className="px-2 py-1 text-xs bg-[#00D68F]/20 text-[#00D68F] rounded">Save</button>
            <button onClick={() => setEditing(false)} className="px-2 py-1 text-xs bg-white/10 rounded">Cancel</button>
          </td>
        </>
      ) : (
        <>
          <td className="text-[#00D68F]">${(inv.returns || 0).toLocaleString()}</td>
          <td className="capitalize">
            <span className={
              inv.status === "pending" ? "text-yellow-300" :
              inv.status === "active" ? "text-[#00D68F]" :
              inv.status === "rejected" ? "text-red-400" : "text-white/70"
            }>{inv.status}</span>
          </td>
          <td className="flex gap-1 py-2">
            {inv.status === "pending" ? (
              <>
                <button onClick={approve} className="px-3 py-1 text-xs bg-[#00D68F]/20 text-[#00D68F] rounded hover:bg-[#00D68F]/30">Approve</button>
                <button onClick={reject} className="px-3 py-1 text-xs bg-red-500/20 text-red-300 rounded hover:bg-red-500/30">Reject</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} className="px-3 py-1 text-xs bg-[#D4AF37]/20 text-[#D4AF37] rounded">Edit</button>
            )}
          </td>
        </>
      )}
    </tr>
  );
}

function MiniInput(p) {
  return <input {...p} className="w-24 px-2 py-1 text-xs rounded" />;
}

function Field({ label, ...p }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60 block mb-1">{label}</span>
      <input {...p} className="w-full px-3 py-2 rounded-lg text-sm" />
    </label>
  );
}